import { describeCompetenceReasons, parseCompetenceReasons } from '@zunelo/shared';
/**
 * Zunelo Domain Mappers
 * Pure transformation functions from concrete API contracts into unified AttentionItem models.
 */

import {
  AttentionItem,
  JiraIssue,
  GitHubPullRequest,
  SlackThread,
  AgentSession,
  AgentItem,
  GmailMessage,
  GmailThread,
  formatRelativeTime,
  AttentionStatus,
  JiraIssueType,
  CurrentUserIdentity,
  CrossReference,
} from './types';
import { formatSlackMrkdwn } from './slackMrkdwn';
import {
  buildMergeCopy,
  deriveGitHubAttentionStatus,
  githubKindLabel,
  githubSignalFlags,
  isPullRequestAuthor,
} from './githubMapperHelpers';
import { resolveGitHubAttentionRefs } from './crossCards';
import { resolveGitHubPrHtmlUrl } from './githubPrUrl';
import { resolveSlackTicketAndRefs } from './slackTicketRefs';

export { deriveGitHubAttentionStatus } from './githubMapperHelpers';

const JIRA_ISSUE_TYPES: JiraIssueType[] = ['Story', 'Bug', 'Task', 'Epic', 'Subtask'];

export function attentionStatusForWorkflow(workflow: string): AttentionStatus | undefined {
  if (workflow === 'On Hold') return 'blocked';
  if (workflow === 'Ready for QA') return 'ready';
  if (workflow === 'Done' || workflow === 'Cerrado' || workflow === 'In QA') return 'them';
  if (['IN DEV', 'En curso', 'Pull request'].includes(workflow)) return 'you';
  return undefined;
}

/** Build Attention cross-refs from Jira issue links (explicit Atlassian relationships). */
export function refsFromIssueLinks(
  links: JiraIssue['fields']['issueLinks'] | undefined,
): CrossReference[] {
  if (!links?.length) return [];
  return links.map((link) => ({
    key: link.linkedIssueKey,
    grade: 'explícita',
    why: link.relationship || 'Relacionada',
  }));
}

/** Merge meta refs with issue-link refs; meta wins on duplicate keys. */
export function mergeJiraRefs(
  metaRefs: CrossReference[] | undefined,
  links: JiraIssue['fields']['issueLinks'] | undefined,
): CrossReference[] | undefined {
  const fromLinks = refsFromIssueLinks(links);
  if (!metaRefs?.length && !fromLinks.length) return undefined;
  const byKey = new Map<string, CrossReference>();
  for (const ref of fromLinks) byKey.set(ref.key, ref);
  for (const ref of metaRefs ?? []) byKey.set(ref.key, ref);
  return [...byKey.values()];
}

export function toJiraIssueType(value: string | undefined): JiraIssueType {
  if (value && JIRA_ISSUE_TYPES.includes(value as JiraIssueType)) {
    return value as JiraIssueType;
  }
  return 'Story';
}

/** Epics are hierarchy context, not default actionable inbox rows. */
export function isActionableJiraIssue(issue: JiraIssue): boolean {
  return issue.fields.issueType !== 'Epic';
}

export function deriveJiraAttentionStatus(issue: JiraIssue): AttentionStatus {
  if (issue.zuneloMeta?.status) {
    return issue.zuneloMeta.status;
  }
  const workflow = issue.fields.status.trim().toLowerCase();
  const hasBlockedCriterion = issue.fields.acceptanceCriteria.some(
    (criterion) => (criterion.status || criterion.state) === 'blocked',
  );
  const hasBlockedSubtask = issue.fields.subtasks.some((subtask) => subtask.status === 'Bloqueado');
  if (workflow === 'on hold' || hasBlockedCriterion || hasBlockedSubtask) {
    return 'blocked';
  }
  if (workflow === 'ready for qa') {
    const allDone = issue.fields.acceptanceCriteria.every(
      (criterion) => (criterion.status || criterion.state) === 'done',
    );
    return allDone ? 'ready' : 'you';
  }
  if (workflow === 'done' || workflow === 'cerrado') {
    return 'them';
  }
  if (['in dev', 'en curso', 'pull request'].includes(workflow)) {
    return 'you';
  }
  return 'them';
}

export function deriveSlackAttentionStatus(thread: SlackThread): AttentionStatus {
  if (thread.zuneloMeta?.status) {
    return thread.zuneloMeta.status;
  }
  if (thread.ballInCourt === 'them') {
    return 'them';
  }
  if (thread.mentionsCurrentUser || thread.unread) {
    return 'you';
  }
  return 'them';
}

export function deriveSlackTags(thread: SlackThread): string[] {
  if (thread.tags && thread.tags.length > 0) {
    return thread.tags;
  }
  if (thread.zuneloMeta?.tags && thread.zuneloMeta.tags.length > 0) {
    return thread.zuneloMeta.tags;
  }
  const tags: string[] = [];
  const ballElsewhere = thread.ballInCourt === 'them' || thread.zuneloMeta?.status === 'them';
  const wantsMyAction =
    !ballElsewhere &&
    (thread.unread || thread.mentionsCurrentUser || thread.implicitlyAddressesUser);
  if (wantsMyAction) {
    tags.push('Atención');
  }
  if (thread.implicitlyAddressesUser) {
    tags.push('Te nombraron');
  } else if (thread.mentionsCurrentUser) {
    tags.push('Menciones');
  }
  if (thread.competenceReasons?.length) {
    tags.push('Tu ámbito');
  }
  const asksQuestion = thread.messages.some((message) => message.text.includes('?'));
  if (asksQuestion) {
    tags.push('Decisiones');
  }
  if (!tags.includes('Atención')) {
    tags.push('Seguimiento');
  }
  return tags;
}

export interface BallInCourtChip {
  label: string;
  tone: 'warn' | 'flat';
}

export function slackBallInCourtChip(
  item: Pick<AttentionItem, 'st' | 'rawSlackThread'>,
): BallInCourtChip | null {
  if (item.st === 'you') {
    return { label: 'Te toca responder', tone: 'warn' };
  }
  if (item.rawSlackThread?.ballInCourt === 'them') {
    return { label: 'Esperas respuesta', tone: 'flat' };
  }
  return null;
}

/** `Tu ámbito · billing · #eng-billing` chip for threads that matched the competence profile. */
export function slackCompetenceChip(
  item: Pick<AttentionItem, 'rawSlackThread'>,
): { label: string; title: string } | null {
  const reasons = parseCompetenceReasons(item.rawSlackThread?.competenceReasons);
  if (!reasons.length) return null;
  const described = describeCompetenceReasons(reasons);
  return {
    label: `Tu ámbito · ${described}`,
    title: `Coincide con tu perfil de avisos: ${described}`,
  };
}

function slackAttentionLead(thread: SlackThread): string {
  if (thread.implicitlyAddressesUser) return 'te nombraron · ';
  if (thread.mentionsCurrentUser) return 'mención directa · ';
  return '';
}

/**
 * Maps a JiraIssue API model to an AttentionItem
 */
export function mapJiraIssueToAttentionItem(
  issue: JiraIssue,
  status?: AttentionStatus,
  aiSummary?: string,
  nextAction?: string,
): AttentionItem {
  const f = issue.fields;
  const meta = issue.zuneloMeta;
  const resolvedStatus = status ?? deriveJiraAttentionStatus(issue);

  return {
    id: issue.key,
    source: 'jira',
    st: resolvedStatus,
    primary: issue.key,
    secondary: `${f.status} · ${f.storyPoints ? `${f.storyPoints} pts · ` : ''}${f.priority}`,
    title: f.summary,
    ai: aiSummary || meta?.ai || 'Sin resumen de IA disponible.',
    next: nextAction || meta?.next || `Actualizar estado de ${issue.key}`,
    action: f.status === 'On Hold' ? 'Ver bloqueo' : 'Editar en Jira',
    age: formatRelativeTime(f.updatedAt),
    updatedAt: f.updatedAt,
    ticket: issue.key,
    workflow: f.status,
    type: f.issueType,
    desc: f.description,
    criteria: f.acceptanceCriteria,
    subtasks: f.subtasks,
    labels: f.labels,
    components: f.components?.length ? [...f.components] : undefined,
    parentEpicKey: f.parentEpicKey,
    parentEpicSummary: f.parentEpicSummary,
    refs: mergeJiraRefs(meta?.refs, f.issueLinks),
    rawJiraIssue: issue,
  };
}

/**
 * Maps a GitHubPullRequest API model to an AttentionItem
 */
export function mapGitHubPRToAttentionItem(
  pr: GitHubPullRequest,
  status?: AttentionStatus,
  aiSummary?: string,
  nextAction?: string,
  currentUser?: CurrentUserIdentity,
): AttentionItem {
  const flags = githubSignalFlags(pr);
  const meta = pr.zuneloMeta;
  const resolvedStatus = status ?? deriveGitHubAttentionStatus(pr, currentUser);
  const isAuthor = isPullRequestAuthor(pr, currentUser);
  const mergeCopy = buildMergeCopy(pr, flags);
  const authorSubtitle = isAuthor ? '' : ` · abre ${pr.author.name || pr.author.login}`;

  return {
    id: pr.id,
    source: 'github',
    role: isAuthor ? 'author' : 'reviewer',
    ticket: pr.linkedTicketKey,
    st: resolvedStatus,
    kind: githubKindLabel(pr, flags.changesRequested),
    primary: `#${pr.number}`,
    secondary: `${pr.head.repo}${authorSubtitle}`,
    age: formatRelativeTime(pr.updatedAt),
    updatedAt: pr.updatedAt,
    title: pr.title,
    action: mergeCopy.mergeCta,
    ai: aiSummary || meta?.ai || 'Checks de CI y revisiones actualizados.',
    next: nextAction || meta?.next || mergeCopy.mergeCta,
    body: pr.bodyLines,
    head: pr.head.ref,
    base: pr.base.ref,
    add: pr.totalAdditions,
    del: pr.totalDeletions,
    commits: `${pr.commitsCount} commits`,
    merged: pr.merged,
    files: pr.changedFiles,
    checks: pr.checkRuns,
    reviewers: pr.reviewers,
    comments: pr.comments,
    mergeTitle: mergeCopy.mergeTitle,
    mergeText: mergeCopy.mergeText,
    mergeCta: mergeCopy.mergeCta,
    refs: resolveGitHubAttentionRefs(pr.linkedTicketKey, meta?.refs),
    htmlUrl:
      resolveGitHubPrHtmlUrl({
        id: pr.id,
        primary: `#${pr.number}`,
        secondary: pr.head.repo,
        htmlUrl: pr.htmlUrl,
        rawPullRequest: pr,
      }) ?? undefined,
    rawPullRequest: pr,
  };
}

/**
 * Maps a SlackThread API model to an AttentionItem
 */
export function mapSlackThreadToAttentionItem(
  thread: SlackThread,
  status?: AttentionStatus,
  aiSummary?: string,
  nextAction?: string,
): AttentionItem {
  const lastMessage = thread.messages[thread.messages.length - 1];
  const members =
    thread.participants.length > 0
      ? thread.participants
      : Array.from(new Set(thread.messages.map((m) => m.user.id))).map(
          (id) => thread.messages.find((m) => m.user.id === id)!.user,
        );
  const meta = thread.zuneloMeta;
  const resolvedStatus = status ?? deriveSlackAttentionStatus(thread);

  const attentionId = thread.attentionId;
  const stableId = attentionId ? attentionId : `SL-${thread.channel.name.replace(/^#/, '')}`;

  const previewText = formatSlackMrkdwn(
    lastMessage?.text ?? thread.aiSummary?.summary ?? thread.channel.name,
  );
  const truncatedTitle =
    previewText.length > 75 ? `«${previewText.slice(0, 75)}…»` : `«${previewText}»`;
  const { ticket, refs } = resolveSlackTicketAndRefs(thread);

  return {
    id: stableId,
    source: 'slack',
    ticket,
    st: resolvedStatus,
    kind: 'Hilo',
    primary: thread.channel.name,
    secondary: `${slackAttentionLead(thread)}${thread.messages.length} mensajes`,
    age: formatRelativeTime(thread.updatedAt),
    updatedAt: thread.updatedAt,
    title: truncatedTitle,
    ai: formatSlackMrkdwn(
      aiSummary ||
        meta?.ai ||
        thread.aiSummary?.summary ||
        'Hilo con actividad reciente en el canal.',
    ),
    next: nextAction || meta?.next || thread.aiSummary?.actionItem || 'Responder al hilo',
    threadMeta: thread.channel.topic || 'Canal de ingeniería y discusiones de producto',
    members,
    // Keep raw mrkdwn — SlackRichText renders emphasis/lists; title/ai still use formatSlackMrkdwn.
    messages: thread.messages,
    tags: deriveSlackTags(thread),
    refs,
    rawSlackThread: thread,
  };
}

/**
 * Maps an AgentSession model to an AgentItem
 */
export function mapAgentSessionToAgentItem(session: AgentSession): AgentItem {
  return {
    ...session,
    name: session.agentName || session.name || 'Agente',
    ticket: session.targetTicketKey || session.ticket || 'DEV-3056',
    branch: session.workingBranch || session.branch || '',
    last: session.lastActivitySummary || session.last || 'En ejecución',
    elapsed: session.elapsedFormatted || session.elapsed || 'reciente',
    tokens: session.tokensFormatted || session.tokens || '0 tok',
    cost: session.costFormatted || session.cost || '$0.00',
    secondAction: session.running ? 'Detener' : 'Reanudar',
  };
}

/**
 * A one-line preview of the mail body, since Gmail messages carry no
 * pre-computed AI summary in production — unlike the mock fixtures this
 * fallback was originally written against. Falls back to an honest
 * "no preview" message only when the body itself is empty.
 */
function gmailBodyPreview(mail: GmailMessage): string {
  const firstParagraph = mail.bodyParagraphs.find((p) => p.trim().length > 0)?.trim();
  if (!firstParagraph) return 'Sin vista previa disponible.';
  return firstParagraph.length > 140 ? `${firstParagraph.slice(0, 140)}…` : firstParagraph;
}

/**
 * Maps a GmailThread model to an AttentionItem
 */
export function mapGmailThreadToAttentionItem(
  thread: GmailThread,
  status?: AttentionStatus,
  aiSummary?: string,
  nextAction?: string,
): AttentionItem {
  const resolvedStatus = status ?? (thread.blocksWork ? 'blocked' : thread.unread ? 'you' : 'them');
  const first = thread.messages[0];
  const last = thread.messages[thread.messages.length - 1];
  const from = first?.from ?? { name: '', email: '' };

  return {
    id: thread.id,
    source: 'gmail',
    ticket: thread.linkedTicketKey,
    st: resolvedStatus,
    kind: thread.kind,
    unread: thread.unread,
    blocksWork: thread.blocksWork,
    primary: from.email,
    secondary: first?.to ?? '',
    age: formatRelativeTime(thread.receivedAt),
    updatedAt: thread.receivedAt,
    title: thread.subject,
    action: 'Responder',
    ai:
      aiSummary ||
      (first
        ? first.aiSummary?.summary || gmailBodyPreview(first)
        : 'Sin vista previa disponible.'),
    next: nextAction || first?.aiSummary?.actionItem || 'Responder correo',
    fromName: from.name,
    fromEmail: from.email,
    initials: from.initials || 'GM',
    color: from.color || 'var(--z-bad)',
    to: first?.to ?? '',
    body: first?.bodyParagraphs,
    quotedHead: last?.quotedHead ?? first?.quotedHead,
    quoted: last?.quotedText ?? first?.quotedText,
    gmailMessages: thread.messages,
    rawGmailThread: thread,
    rawGmailMessage: last ?? first,
  };
}

/**
 * Maps a GmailMessage model to an AttentionItem
 */
export function mapGmailToAttentionItem(
  mail: GmailMessage,
  status?: AttentionStatus,
  aiSummary?: string,
  nextAction?: string,
): AttentionItem {
  const syntheticThread: GmailThread = {
    id: mail.threadId || mail.id,
    messages: [mail],
    subject: mail.subject,
    unread: mail.unread,
    blocksWork: mail.blocksWork,
    kind: mail.kind,
    receivedAt: mail.receivedAt,
    linkedTicketKey: mail.linkedTicketKey,
  };
  const item = mapGmailThreadToAttentionItem(syntheticThread, status, aiSummary, nextAction);
  return {
    ...item,
    id: mail.id,
    rawGmailMessage: mail,
  };
}
